import React from "react";
import { useSelector, useDispatch } from "react-redux";

import {
  Box,
  Grid,
  Chip,
  Switch,
  Checkbox,
  MenuItem,
  TextField,
  FormControl,
  FormControlLabel,
} from "@mui/material";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import { Select } from "@mui/material";
import { Add } from "@mui/icons-material";

import GridListItem from "components/common/grid/GridListItem";
import FormSelectField from "components/FormFields/FormSelect";
import FormTextField from "components/FormFields/FormTextField";
import FormDatePicker from "components/FormFields/FormDatePicker";

import { isValidArray } from "utils/array";

import AddNewDeviceModal from "components/common/modals/addNewDeviceModal/AddNewDeviceModal";
import {
  setPatientInfoToUpdate,
  setShowAddNewDeviceModal,
  setShowReportTemplateUpdateAlert,
} from "app/store/manualUpload/slice/manualUploadSlice";
import PhysiciansInput from "./PhysiciansInput";
import ConfirmableToggleButtons from "components/common/toggle_button/ConfirmableToggleButtons";
import dayjs from "dayjs";
import { getFormattedDate } from "utils/date";
import { DATE_FORMAT } from "static/date";
import RefusesOAC from "components/common/refusesOAC/RefusesOAC";
import FormFieldError from "components/common/form_error/form_field_error";
import ReportTransmissionInformationInput from "./ReportTransmissionInformationInput";
import DeviceInformationInput from "./DeviceInformationInput";
import AssignedPhysiciansInput from "./AssignedPhysiciansInput";

const ReportInfoInput = ({
  fields,
  onUpdate,
  isError,
  devices,
  handleAddNewDevice,
  showAddNewDeviceModal,
  isCrossFacilityClinicUpload,
  isIDTFBilling,
}) => {
  const dispatch = useDispatch();

  const {
    isSubmitting,
    isSubmitClicked,
    selectedPatient,
    reportTemplates,
    isExistingPatient,
    patientManufacturers,
    patientAdditionalManufacturers,
  } = useSelector((state) => state.manualUpload);
  const { currentUser } = useSelector((state) => state.auth);

  const filter = createFilterOptions();

  return (
    <>
      {isExistingPatient && selectedPatient && (
        <>
          <Grid
            container
            className="view-pdf__drawer-grid"
            style={{ marginTop: "20px", marginBottom: "20px" }}
          >
            <Grid item xs={12} className="mt-3">
              <Grid container justifyContent="space-between">
                <GridListItem
                  size="4"
                  title="FIRST NAME"
                  subTitle={fields.firstName ?? "—"}
                />
                <GridListItem
                  size="4"
                  title="LAST NAME"
                  subTitle={fields.lastName ?? "—"}
                />
                <GridListItem
                  size="4"
                  title="EVOKE ID"
                  subTitle={fields?.evoke_id ?? "—"}
                />
              </Grid>
              <Grid container>
                <GridListItem
                  size="4"
                  title="ACCOUNT"
                  subTitle={fields.account ?? "—"}
                />

                <GridListItem
                  size="4"
                  title="DATE OF IMPLANT"
                  subTitle={fields.doi ?? "—"}
                />
              </Grid>
            </Grid>

            <h2
              className="mt-2"
              data-label="manual-upload-general-information-header"
            >
              General Information
            </h2>
            <Grid container spacing={2} style={{ marginBottom: "16px" }}>
              <Grid item xs={6}>
                <FormDatePicker
                  label="Date of Birth"
                  required={true}
                  disabled={isCrossFacilityClinicUpload(fields?.account)}
                  submitted={isSubmitClicked}
                  format={DATE_FORMAT.DEFAULT}
                  value={fields?.dob ? dayjs(fields?.dob) : null}
                  onChange={(date) => {
                    onUpdate({ dob: getFormattedDate(date) });
                  }}
                  hasError={isError("dob") && isSubmitClicked}
                  maxDate={dayjs(new Date())}
                  data-text="manual-upload-dob-field"
                />
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <h2
                className="mt-2"
                data-label="manual-upload-device-information-header"
              >
                Device Information
              </h2>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <FormSelectField
                    label="Manufacturer"
                    required={true}
                    disabled={isCrossFacilityClinicUpload(fields?.account)}
                    options={patientManufacturers}
                    submitClicked={isSubmitClicked}
                    hasError={isError("manufacturer") && isSubmitClicked}
                    customMapping={
                      <Select
                        value={fields.manufacturer}
                        onChange={(e) => {
                          onUpdate({ manufacturer: e.target.value });
                        }}
                        data-text="manual-upload-manufacturer-field"
                      >
                        {patientManufacturers.map((manufacturer, index) => (
                          <MenuItem key={index} value={manufacturer.name}>
                            {manufacturer.name}
                          </MenuItem>
                        ))}
                      </Select>
                    }
                  />
                </Grid>

                <Grid item xs={6}>
                  <FormControl margin="normal" fullWidth>
                    <Autocomplete
                      freeSolo
                      name="device"
                      fullWidth
                      clearOnBlur
                      selectOnFocus
                      className="mb-3"
                      handleHomeEndKeys
                      disabled={isExistingPatient}
                      id="free-solo-with-text-demo"
                      value={fields.device || ""}
                      options={devices?.filter((item) => item) || []}
                      filterOptions={(options, params) => {
                        const filtered = filter(options, params);
                        return filtered;
                      }}
                      getOptionLabel={(option) => option}
                      onChange={(event, newValue) => {
                        onUpdate({ device: newValue });
                      }}
                      data-action="manual-upload-device-autocomplete"
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Device"
                          variant="outlined"
                          placeholder="Device"
                          error={
                            isError("device") &&
                            isSubmitClicked &&
                            !fields.isRpmDevice
                          }
                          InputProps={{
                            ...params.InputProps,
                            readOnly: isSubmitting ? true : false,
                          }}
                        />
                      )}
                      data-text="manual-upload-device-field"
                    />
                    {isError("device") &&
                      isSubmitClicked &&
                      !fields.isRpmDevice && (
                        <FormFieldError errorMessage={`Device is required.`} />
                      )}
                  </FormControl>

                  {!isCrossFacilityClinicUpload(fields?.account) && (
                    <Grid
                      item
                      xs={12}
                      container
                      alignItems="center"
                      justifyContent="flex-end"
                    >
                      <span
                        className="rpm-device-information__add-rpm-device mb-3"
                        onClick={() => {
                          if (reportTemplates.length > 0) {
                            dispatch(setShowReportTemplateUpdateAlert(true));
                            dispatch(
                              setPatientInfoToUpdate({
                                key: "changeDevice",
                                newValue: true,
                              })
                            );
                          } else {
                            dispatch(setShowAddNewDeviceModal(true));
                          }
                        }}
                      >
                        <Add className="rpm-device-information__add-icon" />
                        <span className="rpm-device-information__add-text">
                          Change Device
                        </span>
                      </span>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>

            {fields.isRpmDevice &&
              isValidArray(patientAdditionalManufacturers) && (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormSelectField
                      label="Additional Manufacturer"
                      options={patientAdditionalManufacturers}
                      submitClicked={isSubmitClicked}
                      hasError={
                        isError("additionalManufacturer") && isSubmitClicked
                      }
                      customMapping={
                        <Select
                          multiple
                          style={{ minWidth: "120px" }}
                          id="state"
                          value={fields.additionalManufacturer}
                          renderValue={(selected) => (
                            <Box>
                              {selected.map((manufacturer) => (
                                <Chip
                                  key={manufacturer}
                                  label={manufacturer}
                                  className="mr-1"
                                />
                              ))}
                            </Box>
                          )}
                          onChange={(e) => {
                            onUpdate({
                              additionalManufacturer: e.target.value,
                            });
                          }}
                          data-text="manual-upload-additional-manufacturer-existing-field"
                        >
                          {patientAdditionalManufacturers.map(
                            (manufacturer, index) => (
                              <MenuItem key={index} value={manufacturer.name}>
                                <Checkbox
                                  checked={
                                    fields.additionalManufacturer.indexOf(
                                      manufacturer.name
                                    ) > -1
                                  }
                                />
                                {manufacturer.name}
                              </MenuItem>
                            )
                          )}
                        </Select>
                      }
                    />
                  </Grid>
                </Grid>
              )}
          </Grid>
          <ReportTransmissionInformationInput
            fields={fields}
            isError={isError}
            onUpdate={onUpdate}
            isSubmitClicked={isSubmitClicked}
            currentUser={currentUser}
            isIDTFBilling={isIDTFBilling}
          />
        </>
      )}

      {!isExistingPatient && (
        <>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <FormTextField
                fullWidth
                value={fields.firstName}
                label="First Name"
                onChange={(e) => {
                  onUpdate({ firstName: e.target.value });
                }}
                required={true}
                hasError={isError("firstName") && isSubmitClicked}
                inputProps={{ maxLength: 24 }}
                margin="none"
                data-text="manual-upload-first-name-field"
              />
            </Grid>
            <Grid item xs={6}>
              <FormTextField
                fullWidth
                value={fields.lastName}
                label="Last Name"
                onChange={(e) => {
                  onUpdate({ lastName: e.target.value });
                }}
                required
                hasError={isError("lastName") && isSubmitClicked}
                inputProps={{ maxLength: 24 }}
                margin="none"
                data-text="manual-upload-last-name-field"
              />
            </Grid>
          </Grid>
          {!isExistingPatient && (
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormDatePicker
                  label="Date of Birth"
                  required={true}
                  format={DATE_FORMAT.DEFAULT}
                  value={fields?.dob ? dayjs(fields?.dob) : null}
                  submitted={isSubmitClicked}
                  onChange={(date) => {
                    onUpdate({ dob: getFormattedDate(date) });
                  }}
                  hasError={isError("dob") && isSubmitClicked}
                  maxDate={dayjs(new Date())}
                  data-text="manual-upload-new-patient-dob-field"
                />
              </Grid>
              <Grid item xs={6}>
                <FormDatePicker
                  label="Date of Implant"
                  format={DATE_FORMAT.DEFAULT}
                  required={!fields?.isRpmDevice}
                  value={fields?.doi ? dayjs(fields?.doi) : null}
                  submitted={isSubmitClicked}
                  onChange={(date) => {
                    onUpdate({ doi: getFormattedDate(date) });
                  }}
                  hasError={
                    !fields?.isRpmDevice && isError("doi") && isSubmitClicked
                  }
                  maxDate={dayjs(new Date())}
                  margin="none"
                  data-text="manual-upload-doi-field"
                />
              </Grid>
            </Grid>
          )}

          {!isExistingPatient && (
            <DeviceInformationInput
              fields={fields}
              onUpdate={onUpdate}
              isError={isError}
              devices={devices}
            />
          )}

          <ReportTransmissionInformationInput
            fields={fields}
            isError={isError}
            onUpdate={onUpdate}
            isSubmitClicked={isSubmitClicked}
            currentUser={currentUser}
            isIDTFBilling={isIDTFBilling}
          />
        </>
      )}
      {(selectedPatient || !isExistingPatient) && (
        <>
          <h2
            style={{ marginTop: "16px" }}
            data-text="manual-upload-additional-information-header"
          >
            Additional Information
          </h2>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={6}>
              <FormTextField
                fullWidth
                value={fields.mrn}
                disabled={isCrossFacilityClinicUpload(fields?.account)}
                label="MRN"
                inputProps={{ maxLength: 30 }}
                onChange={(e) => {
                  onUpdate({ mrn: e.target.value });
                }}
                margin="none"
                data-text="manual-upload-mrn-field"
              />
            </Grid>

            <Grid item xs={6}>
              <FormControlLabel
                label={"LAAO Device"}
                labelPlacement="start"
                control={
                  <Switch
                    name="laaoDevice"
                    checked={fields.laaoDevice}
                    disabled={isCrossFacilityClinicUpload(fields?.account)}
                    onChange={(event) => {
                      const newLaaoDeviceStatus = event.target.checked;
                      onUpdate({ laaoDevice: newLaaoDeviceStatus });
                    }}
                    data-action="manual-upload-laao-device-switch"
                  />
                }
                style={{ marginLeft: 0, marginRight: "2px" }}
                className="toggle-label"
              />
            </Grid>
          </Grid>
          <PhysiciansInput
            fields={fields}
            onUpdate={onUpdate}
            isCrossFacilityClinicUpload={isCrossFacilityClinicUpload}
          />

          {isCrossFacilityClinicUpload(fields?.account) && (
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={4}>
                <FormTextField
                  fullWidth
                  value={fields?.reportAccount}
                  disabled={true}
                  label="Account"
                  inputProps={{ maxLength: 30 }}
                  margin="none"
                />
              </Grid>

              <Grid item xs={8}>
                <AssignedPhysiciansInput fields={fields} onUpdate={onUpdate} />
              </Grid>
            </Grid>
          )}

          <Grid container spacing={2} alignItems="center">
            <Grid item>
              <span
                className="anticoag-status-title"
                style={{ fontSize: "14px" }}
              >
                Anticoagulants Status
              </span>
              <ConfirmableToggleButtons
                size="small"
                value={fields.anticoagStatus}
                disabled={isCrossFacilityClinicUpload(fields?.account)}
                onChange={(event) => {
                  const newValue = event.target.value;
                  onUpdate({
                    anticoagStatus: newValue,
                    refusesOAC: fields.refusesOAC && newValue === "no",
                  });
                }}
                showConfirmWhen={(newValue) =>
                  fields.refusesOAC && newValue !== "no"
                }
                confirmTitle="Confirm Status Change"
                confirmMessage={
                  <>
                    Changing the status will update the
                    <b> Anticoagulant Status</b> and remove the{" "}
                    <b>Refuses OAC</b>. Are you sure you want to continue?
                  </>
                }
                confirmButtonText="Confirm"
                cancelButtonText="Cancel"
                data-action="manual-upload-anticoag-status-toggle"
                buttons={[
                  { value: "yes", color: "success", label: "Yes" },
                  { value: "no", color: "error", label: "No" },
                  { value: "unknown", label: "Unknown" },
                ]}
                data-text="manual-upload-anticoagulant-field"
              />
            </Grid>
            <Grid lg={5} item>
              <FormTextField
                fullWidth
                value={fields.anticoag}
                label="Anticoagulant"
                disabled={isCrossFacilityClinicUpload(fields?.account)}
                required={fields.anticoagStatus === "yes"}
                inputProps={{ maxLength: 50 }}
                hasError={
                  fields.anticoagStatus === "yes" &&
                  !fields.anticoag &&
                  isSubmitClicked
                }
                onChange={(e) => {
                  onUpdate({ anticoag: e.target.value });
                }}
              />
            </Grid>

            <Grid item lg={2}>
              <RefusesOAC
                refusesOAC={fields.refusesOAC}
                anticoagStatus={fields.anticoagStatus}
                handleRefuseOAC={(refusesOAC, removeAnticoagStatus) => {
                  onUpdate({
                    refusesOAC,
                    ...(removeAnticoagStatus ? { anticoagStatus: "no" } : {}),
                  });
                }}
                disable={isCrossFacilityClinicUpload(fields?.account)}
              />
            </Grid>
          </Grid>
        </>
      )}
      {selectedPatient && showAddNewDeviceModal && (
        <AddNewDeviceModal
          isManualUpload={true}
          currentDevice={selectedPatient?.device}
          patientId={selectedPatient?.id}
          isModalOpen={showAddNewDeviceModal}
          handleAddNewDevice={handleAddNewDevice}
          onClose={() => dispatch(setShowAddNewDeviceModal(false))}
        />
      )}
    </>
  );
};

export default ReportInfoInput;
